import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createPortal } from 'react-dom';
import { api } from '../../lib/api.js';
import { toast } from '../../lib/toast.js';
import { ABSENCE_TYPE_LABELS, type AbsenceType } from '@cls/shared';

interface Props {
  onClose: () => void;
  workerId: string;
  workerName: string;
  initialType?: AbsenceType;
}

export function AbsenceModal({ onClose, workerId, workerName, initialType = 'sick' }: Props) {
  const qc = useQueryClient();
  const today = new Date().toISOString().slice(0, 10);
  const [type, setType] = useState<AbsenceType>(initialType);
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const save = useMutation({
    mutationFn: async () => api.post('/absences', {
      workerId,
      type,
      startDate,
      endDate,
      notes: notes.trim() || null,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['absences'] });
      toast.success(`${ABSENCE_TYPE_LABELS[type]} recorded`, `${workerName} · ${startDate} → ${endDate}`);
      onClose();
    },
    onError: (e: any) => toast.error('Save failed', e?.response?.data?.issues?.[0]?.message ?? 'Check the dates and try again'),
  });

  const invalid = !startDate || !endDate || endDate < startDate;

  return createPortal(
    <div className="mo open" onClick={onClose}>
      <div className="modal" style={{ width: 460 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-title">{type === 'sick' ? '🤒' : '🏖'} Record {ABSENCE_TYPE_LABELS[type]} Period</div>
        <div className="modal-sub" style={{ marginBottom: 14 }}>
          {workerName} — the deployment stays in place; these days show as {ABSENCE_TYPE_LABELS[type].toLowerCase()} on the calendar.
        </div>
        <div className="form-row">
          <label className="form-label">Type</label>
          <select className="form-select" id="ab-type" value={type} onChange={(e) => setType(e.target.value as AbsenceType)}>
            <option value="sick">🤒 Sick</option>
            <option value="holiday">🏖 Holiday</option>
          </select>
        </div>
        <div className="fg2">
          <div className="form-row"><label className="form-label">From</label><input type="date" className="form-input" id="ab-start" value={startDate} onChange={(e) => setStartDate(e.target.value)} /></div>
          <div className="form-row"><label className="form-label">To (inclusive)</label><input type="date" className="form-input" id="ab-end" value={endDate} min={startDate} onChange={(e) => setEndDate(e.target.value)} /></div>
        </div>
        <div className="form-row"><label className="form-label">Notes</label><textarea className="form-textarea" id="ab-notes" style={{ minHeight: 50 }} placeholder="Optional — reason, expected return…" value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
        <div className="modal-actions">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={() => save.mutate()} disabled={save.isPending || invalid}>
            {save.isPending ? 'Saving…' : `✓ Record ${ABSENCE_TYPE_LABELS[type]}`}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
